// A recording's exact length and a small static waveform, worked out once at
// record/upload time and stored with it (Narration.peaks, VoiceBlock.peaks),
// so readers never decode audio just to draw bars.
//
// Decoding uses Web Audio. Where that's missing or the codec isn't supported
// (jsdom, some Safari/WebM mixes) it falls back to the <audio> element's
// metadata for the length and flat bars for the shape.

export const PEAK_COUNT = 40

export interface AudioAnalysis {
  /** Seconds; 0 when it couldn't be worked out at all. */
  duration: number
  /** `count` values 0..1, loudest bucket = 1. */
  peaks: number[]
  /** false when the browser couldn't decode it (flat peaks, metadata or fallback duration). */
  decoded: boolean
}

/** Even bars for when the real shape is unknown. */
export function flatPeaks(count = PEAK_COUNT, level = 0.35): number[] {
  return Array.from({ length: count }, () => level)
}

/** Scale so the loudest value is 1, keeping a small floor so quiet bars stay visible. */
export function normalisePeaks(values: number[], floor = 0.06): number[] {
  const max = values.reduce((m, v) => (v > m ? v : m), 0)
  if (!(max > 0)) return values.map(() => floor)
  return values.map((v) => Math.round(Math.max(floor, v / max) * 100) / 100)
}

/**
 * Loudness (RMS) per bucket across all channels, normalised. RMS rather than
 * the raw peak: every second of speech hits a similar peak, but its RMS dips
 * in the pauses, which is what makes a voice waveform readable.
 */
export function computePeaks(channels: ArrayLike<number>[], count = PEAK_COUNT): number[] {
  const length = channels[0]?.length ?? 0
  if (length === 0 || count <= 0) return flatPeaks(count)
  const size = length / count
  // Long files: sample each bucket instead of walking every sample.
  const stride = Math.max(1, Math.floor(size / 2000))
  const out: number[] = new Array(count)
  for (let i = 0; i < count; i++) {
    const from = Math.floor(i * size)
    const to = Math.max(from + 1, Math.min(length, Math.floor((i + 1) * size)))
    let sum = 0
    let n = 0
    for (const ch of channels) {
      for (let j = from; j < to; j += stride) {
        const v = ch[j]
        sum += v * v
        n++
      }
    }
    out[i] = n ? Math.sqrt(sum / n) : 0
  }
  return normalisePeaks(out)
}

/** Fit stored peaks to at most `max` bars (loudest in each group), for drawing. */
export function resamplePeaks(peaks: number[], max = PEAK_COUNT): number[] {
  if (peaks.length <= max) return peaks
  const size = peaks.length / max
  return Array.from({ length: max }, (_, i) => {
    let m = 0
    for (let j = Math.floor(i * size); j < Math.floor((i + 1) * size); j++) m = Math.max(m, peaks[j])
    return m
  })
}

export interface AnalyseOptions {
  count?: number
  /** Seconds to report when neither decoding nor metadata gives a length (e.g. the recorder's clock). */
  fallbackDuration?: number
  /** How long to wait for <audio> metadata. */
  timeoutMs?: number
}

export async function analyseAudio(blob: Blob, opts: AnalyseOptions = {}): Promise<AudioAnalysis> {
  const count = opts.count ?? PEAK_COUNT
  try {
    const buffer = await decode(blob)
    if (buffer && buffer.duration > 0) {
      const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i))
      return { duration: buffer.duration, peaks: computePeaks(channels, count), decoded: true }
    }
  } catch {
    /* unsupported codec or corrupt file: fall back below */
  }
  const fromMetadata = await metadataDuration(blob, opts.timeoutMs ?? 4000)
  return { duration: fromMetadata ?? opts.fallbackDuration ?? 0, peaks: flatPeaks(count), decoded: false }
}

type OfflineCtor = typeof OfflineAudioContext
type OnlineCtor = typeof AudioContext

async function decode(blob: Blob): Promise<AudioBuffer | null> {
  const w = window as unknown as {
    OfflineAudioContext?: OfflineCtor
    webkitOfflineAudioContext?: OfflineCtor
    AudioContext?: OnlineCtor
    webkitAudioContext?: OnlineCtor
  }
  const Offline = w.OfflineAudioContext ?? w.webkitOfflineAudioContext
  const Online = w.AudioContext ?? w.webkitAudioContext
  if (!Offline && !Online) return null
  const data = await readArrayBuffer(blob)

  // decodeAudioData resamples to the context's rate. 8 kHz is plenty for a
  // 40-bar outline and keeps a 20-minute narration to a few tens of MB.
  let ctx: BaseAudioContext | null = null
  let online: AudioContext | null = null
  if (Offline) {
    for (const rate of [8000, 22050, 44100]) {
      try {
        ctx = new Offline(1, 1, rate)
        break
      } catch {
        /* rate not supported here; try the next */
      }
    }
  }
  if (!ctx && Online) ctx = online = new Online()
  if (!ctx) return null
  const context = ctx
  try {
    return await new Promise<AudioBuffer>((resolve, reject) => {
      // Older Safari only has the callback form; newer engines return a promise too.
      const p = context.decodeAudioData(data, resolve, reject) as Promise<AudioBuffer> | undefined
      p?.then?.(resolve, reject)
    })
  } finally {
    void online?.close().catch(() => {})
  }
}

function readArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the audio.'))
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * Length from an <audio> element's metadata. Chrome reports Infinity for
 * MediaRecorder WebM until it has seeked to the end, so nudge it there.
 */
function metadataDuration(blob: Blob, timeoutMs: number): Promise<number | null> {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return Promise.resolve(null)
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob)
    const el = document.createElement('audio')
    let settled = false
    const finish = (value: number | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      el.removeAttribute('src')
      el.load?.()
      URL.revokeObjectURL(url)
      resolve(value)
    }
    const check = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) finish(el.duration)
      else if (el.duration === Infinity) el.currentTime = 1e101
    }
    const timer = setTimeout(() => finish(null), timeoutMs)
    el.preload = 'metadata'
    el.addEventListener('loadedmetadata', check)
    el.addEventListener('durationchange', check)
    el.addEventListener('error', () => finish(null))
    el.src = url
  })
}
